import { ref, computed, watch } from 'vue'
import { useConsentStore } from '@/stores/consent.store'
import { useGtagConversion } from '@/composables/useGtagConversion'
import { initTracking } from '@/utils/initTracking'
import { trackingConfig } from '@/config/tracking'
import { env } from '@/utils/env'
import { setTrustedScriptSrc } from '@/utils/trustedScriptSrc'

let _wired = false // ensures we only wire watchers once per app

export function useTracking() {
  const consentStore = useConsentStore()

  // reactive API for consumers
  const consent = computed(() => ({ categories: consentStore.categories }))
  const isReady = ref(false)

  // gtag conversion helper (kept as-is)
  const consentObjectForGtag = computed(() =>
    consent.value ? { categories: consent.value.categories } : null
  )
  const { reportConversion } = useGtagConversion(consentObjectForGtag)

  if (!_wired) {
    _wired = true

    if (env.isDev) console.info('[Tracking] wiring watchers…')

    let gtmLoaded = false

    const maybeLoadGtm = () => {
      if (gtmLoaded) return
      if (!trackingConfig.isEnabled || !trackingConfig.gtm?.containerId) return

      const id = trackingConfig.gtm.containerId
      if (!id) return

      // Typisk: "GTM-XXXXXXX" (bokstaver/tall)
      if (!/^GTM-[A-Z0-9]+$/i.test(id)) {
        if (env.isDev) console.warn('[Tracking] Invalid GTM containerId:', id)
        return
      }

      const src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(id)}`

      // Eksistens-sjekk: matcher URL robust
      const exists = Array.from(document.scripts).some((s) => {
        try {
          const u = new URL(s.src)
          return (
            u.origin === 'https://www.googletagmanager.com' &&
            u.pathname === '/gtm.js' &&
            u.searchParams.get('id') === id
          )
        } catch {
          return false
        }
      })

      if (exists) {
        gtmLoaded = true
        if (env.isDev) console.info('[Tracking] GTM already present:', id)
        return
      }

      const script = document.createElement('script')
      setTrustedScriptSrc(script, src)
      script.async = true
      document.head.appendChild(script)

      gtmLoaded = true
      if (env.isDev) console.info('[Tracking] GTM container loaded:', id)
    }

    // single watcher handles both initial state and later changes
    watch(
      () => consentStore.categories,
      (newCategories, oldCategories) => {
        if (env.isDev) {
          console.groupCollapsed('[Tracking] categories changed')
          console.info('old:', oldCategories)
          console.info('new:', newCategories)
          console.groupEnd()
        }

        if (!newCategories || newCategories.length === 0) {
          if (env.isDev) console.info('[Tracking] no consent yet — waiting…')
          return
        }

        if (env.isDev) {
          console.groupCollapsed('[Tracking:debug]')
          console.info('Environment:', env.appEnv)
          console.info('Consent categories:', newCategories)
          console.info('Analytics allowed:', newCategories.includes('analytics'))
          console.info(
            'Marketing allowed:',
            newCategories.includes('advertisement') || newCategories.includes('marketing'),
          )
          console.info('Dev mode - tracker initialization skipped.')
          console.groupEnd()
          return
        }

        // load GTM once (only when we actually have consent info)
        maybeLoadGtm()

        // (re)initialize downstream trackers with current consent
        initTracking({ categories: newCategories })

        if (env.isDev) {
          if (!isReady.value) {
            console.info('[Tracking] initialized with consent:', newCategories)
          } else {
            console.info('[Tracking] reinitialized after consent change:', newCategories)
          }
        }
        isReady.value = true
      },
      { immediate: true, deep: true }
    )
  }

  return { consent, reportConversion, isReady }
}
